// Your provided carousel component, with a small listener for rvp:rendered

  class carouselComponentCustom extends HTMLElement {
    constructor() {
      super();
      this.swiperIns = null;
      this.carousel = null;
      this.next = null;
      this.prev = null;
      this.paginationEl = null;
    }

    connectedCallback() {
      if (!this.hasListener) {
        this.addEventListener('rvp:rendered', () => {
          if (this.swiperIns) {
            this.swiperIns.update();
            this.setSwiperHeight();
          } else {
            this.initSlider();
          }
        });
        this.hasListener = true;
      }

      // If slides already exist (SSR/fast render), init immediately
      if (!this.swiperIns && this.querySelector('.swiper-wrapper > *')) {
        this.initSlider();
      }
    }

    initSlider() {
      if (this.swiperIns) return; // avoid double init
      if (typeof Swiper === 'undefined') {
        console.warn('[carousel-component] Swiper not found on window.');
        return;
      }

      this.carousel = this.querySelector('.swiper');
      if (!this.carousel) return;

      // Retrieve settings
      this.next = this.carousel.querySelector('[id^="swiper-next-"]');
      this.prev = this.carousel.querySelector('[id^="swiper-prev-"]');
      this.paginationEl = this.carousel.querySelector('.swiper-pagination');

      const getBool = (key, def = false) => {
        const v = this.carousel.getAttribute(key);
        if (v === null) return def;
        return String(v) === 'true';
      };
      const getNum = (key, def = 0) => {
        const v = parseFloat(this.carousel.getAttribute(key));
        return isNaN(v) ? def : v;
      };

      this.loop = getBool('data-loop', true);
      this.arrow = getBool('data-arrow', true);
      this.pagination = getBool('data-pagination', true);
      this.autoplay = getBool('data-autoplay', true);
      this.interval = getNum('data-interval', 5000);
      this.transition = getNum('data-transition', 300);

      this.desktop_item = getNum('data-desktop-columns', 4);
      this.tab_item = getNum('data-tabs-columns', 3);
      this.mobile_item = getNum('data-mobile-columns', 1.2);

      this.desktop_spacing = getNum('data-desktop-spacing-grid-horizontal', 20);
      this.spacing = getNum('data-mobile-spacing-grid-horizontal', 12);

      // NaN fallbacks
      this.tab_item = isNaN(this.tab_item) ? this.mobile_item : this.tab_item;
      this.desktop_item = isNaN(this.desktop_item) ? this.tab_item : this.desktop_item;
      this.desktop_spacing = isNaN(this.desktop_spacing) ? 0 : this.desktop_spacing;
      this.spacing = isNaN(this.spacing) ? 0 : this.spacing;

      const breakpoints = {
        640:  { slidesPerView: this.mobile_item,  spaceBetween: this.spacing },
        768:  { slidesPerView: this.tab_item,     spaceBetween: this.desktop_spacing },
        1024: { slidesPerView: this.desktop_item, spaceBetween: this.desktop_spacing }
      };

      this.swiperIns = new Swiper(this.carousel, {
        loop: this.loop,
        spaceBetween: this.spacing,
        autoplay: this.autoplay ? {
          delay: this.interval,
          disableOnInteraction: false,
          pauseOnMouseEnter: true
        } : false,
        speed: this.transition,
        slidesPerView: this.mobile_item,
        pagination: this.pagination ? { el: this.paginationEl, clickable: true } : false,
        navigation: this.arrow ? { nextEl: this.next, prevEl: this.prev } : false,
        breakpoints,
        on: {
          init: () => this.setSwiperHeight(),
          resize: () => this.setSwiperHeight(),
          imagesReady: () => this.setSwiperHeight()
        }
      });
    }

    slideAutoplayStop() { return this.swiperIns?.autoplay?.stop(); }
    slideAutoplayStart() { return this.swiperIns?.autoplay?.start(); }
    carouselSlideTo(i)   { return this.loop ? this.swiperIns?.slideToLoop(i) : this.swiperIns?.slideTo(i); }

    destroySwiper() {
      if (this.swiperIns) {
        this.swiperIns.destroy(true, true);
        this.swiperIns = null;
      }
    }

    setSwiperHeight() {
      const wrapper = this.carousel?.querySelector('.swiper-wrapper');
      if (wrapper) this.style.setProperty('--swiper-height', `${wrapper.offsetHeight}px`);
    }
  }

  if (!customElements.get('carousel-component-custom')) {
    customElements.define('carousel-component-custom', carouselComponentCustom);
  }



// collection List Tab
class collectionListTab extends HTMLElement {
    constructor() {
        super();

        // Support both: role="tab" (current markup) and legacy [data-tab-target]
        this.allTabs = Array.from(
            this.querySelectorAll("[role='tab'], [data-tab-target]")
        );

        // Panels are proper ARIA tabpanels in your markup
        this.allPanels = Array.from(this.querySelectorAll("[role='tabpanel']"));

        this.pairs = [];
        this._uid = "ct" + Math.random().toString(36).slice(2);
    }

    connectedCallback() {
        if (!this.allTabs.length || !this.allPanels.length) return;

        // Build tab → panel pairs
        this.pairs = this.allTabs
            .map((tab, i) => {
                let panel = null;

                // 1) aria-controls (primary in your markup)
                const ac = tab.getAttribute("aria-controls");
                if (ac) panel = this.querySelector("#" + CSS.escape(ac)) || document.getElementById(ac);

                // 2) data-tab-target (fallback if present)
                if (!panel) {
                    const sel = tab.dataset.tabTarget;
                    if (sel) panel = this.querySelector(sel) || document.querySelector(sel);
                }

                // 3) index fallback (last resort if counts match)
                if (!panel && this.allPanels[i]) panel = this.allPanels[i];

                return panel ? { tab, panel } : null;
            })
            .filter(Boolean);

        if (!this.pairs.length) return;

        // Ensure a tablist container has role
        const tablist = this.querySelector('[role="tablist"]') || this.pairs[0].tab.parentElement;
        if (tablist && tablist.getAttribute("role") !== "tablist") {
            tablist.setAttribute("role", "tablist");
            tablist.setAttribute("aria-orientation", "horizontal");
        }

        // Init ARIA & hidden states (don’t rely on initial markup)
        this.pairs.forEach((pair, i) => this.setupAriaForPair(pair, i));

        // Initial active: aria-selected="true" → .active → first
        let activeIndex = this.pairs.findIndex(p => p.tab.getAttribute("aria-selected") === "true");
        if (activeIndex < 0) activeIndex = this.pairs.findIndex(p => p.tab.classList.contains("active"));
        if (activeIndex < 0) activeIndex = 0;

        this.activate(activeIndex, false);

        // Bind events
        this.pairs.forEach((pair, i) => {
            pair.tab.addEventListener("click", () => this.activate(i, false));
            pair.tab.addEventListener("keydown", (e) => this.onTabKeydown(e, i));
        });
    }

    setupAriaForPair({ tab, panel }, index) {
        // Tab role/id
        if (tab.getAttribute("role") !== "tab") tab.setAttribute("role", "tab");
        if (!tab.id) tab.id = `${this._uid}-tab-${index}-trigger`;

        // Panel role/id
        if (panel.getAttribute("role") !== "tabpanel") panel.setAttribute("role", "tabpanel");
        if (!panel.id) panel.id = `${this._uid}-panel-${index}`;

        // Link them
        tab.setAttribute("aria-controls", panel.id);
        panel.setAttribute("aria-labelledby", tab.id);

        // Default (will be corrected by activate)
        tab.setAttribute("aria-selected", "false");
        tab.setAttribute("tabindex", "-1");
        panel.hidden = true;
    }

    activate(index, moveFocus) {
        if (index < 0 || index >= this.pairs.length) return;

        this.pairs.forEach(({ tab, panel }, i) => {
            const isActive = i === index;

            // Keep your class API
            tab.classList.toggle("active", isActive);
            panel.classList.toggle("active", isActive);

            // ARIA + focusability
            tab.setAttribute("aria-selected", String(isActive));
            tab.setAttribute("tabindex", isActive ? "0" : "-1");
            panel.hidden = !isActive;

            if (isActive && moveFocus) tab.focus();
        });
    }

    onTabKeydown(e, i) {
        const key = e.key;
        if (key === "ArrowRight" || key === "ArrowLeft" || key === "Home" || key === "End") {
            e.preventDefault();
            const last = this.pairs.length - 1;
            let next = i;

            if (key === "ArrowRight") next = (i + 1) % this.pairs.length;
            else if (key === "ArrowLeft") next = (i - 1 + this.pairs.length) % this.pairs.length;
            else if (key === "Home") next = 0;
            else if (key === "End") next = last;

            this.activate(next, true);
        } else if (key === "Enter" || key === " ") {
            e.preventDefault();
            this.activate(i, false);
        }
    }
}
customElements.define("collection-tab", collectionListTab);
class carouselComponent extends HTMLElement {
    constructor() {
        super();
        this.swiperIns = null;
    }

    connectedCallback() {
        // অটো-ইনিট; ডাবল ইনিট এড়াও
        if (this.swiperIns) return;
        this.initSlider();
    }

    initSlider() {
        // Swiper গ্লোবাল না থাকলে soft-fail
        if (typeof Swiper === 'undefined') {
            console.warn('[carousel-component] Swiper not found on window.');
            return;
        }

        this.carousel = this.querySelector('[id^="carousel-"]');
        if (!this.carousel || this.swiperIns) return;

        // Retrieve settings
        this.next = this.carousel.querySelector('[id^="swiper-next-"]');
        this.prev = this.carousel.querySelector('[id^="swiper-prev-"]');
        this.paginationEl = this.carousel.querySelector(".swiper-pagination");

        this.loop = this.carousel.getAttribute('data-loop') === 'true';
        this.arrow = this.carousel.getAttribute('data-arrow') === 'true';
        this.pagination = this.carousel.getAttribute('data-pagination') === 'true';
        this.autoplay = this.carousel.getAttribute('data-autoplay') === 'true';
        this.interval = parseInt(this.carousel.getAttribute('data-interval'), 10) || 5000;
        this.transition = parseInt(this.carousel.getAttribute('data-transition'), 10) || 300;

        this.desktop_item = parseInt(this.carousel.getAttribute('data-desktop-columns'));
        this.tab_item = parseInt(this.carousel.getAttribute('data-tabs-columns'));
        this.mobile_item = parseFloat(this.carousel.getAttribute('data-mobile-columns'));

        this.desktop_spacing = parseInt(this.carousel.getAttribute('data-desktop-spacing-grid-horizontal'));
        this.spacing = parseInt(this.carousel.getAttribute('data-mobile-spacing-grid-horizontal'));

        // NaN fallbacks
        this.tab_item = isNaN(this.tab_item) ? this.mobile_item : this.tab_item;
        this.desktop_item = isNaN(this.desktop_item) ? this.tab_item : this.desktop_item;
        this.desktop_spacing = isNaN(this.desktop_spacing) ? 0 : this.desktop_spacing;
        this.spacing = isNaN(this.spacing) ? 0 : this.spacing;

        const breakpoints = {
            640:  { slidesPerView: this.mobile_item,  spaceBetween: this.spacing },
            768:  { slidesPerView: this.tab_item,     spaceBetween: this.desktop_spacing },
            1024: { slidesPerView: this.desktop_item, spaceBetween: this.desktop_spacing },
        };

        this.swiperIns = new Swiper(this.carousel, {
            loop: this.loop,
            spaceBetween: this.spacing,
            autoplay: this.autoplay ? {
                delay: this.interval,
                disableOnInteraction: false,
                pauseOnMouseEnter: true,
            } : false,
            speed: this.transition,
            slidesPerView: this.mobile_item,
            pagination: this.pagination ? { el: this.paginationEl, clickable: true } : false,
            navigation: this.arrow ? { nextEl: this.next, prevEl: this.prev } : false,
            breakpoints,
            on: {
                init: () => this.setSwiperHeight(),
                resize: () => this.setSwiperHeight(),
                imagesReady: () => this.setSwiperHeight(),
            },
        });
    }

    slideAutoplayStop() { return this.swiperIns?.autoplay?.stop(); }
    slideAutoplayStart() { return this.swiperIns?.autoplay?.start(); }
    carouselSlideTo(i)   { return this.loop ? this.swiperIns?.slideToLoop(i) : this.swiperIns?.slideTo(i); }

    destroySwiper() {
        if (this.swiperIns) {
            this.swiperIns.destroy(true, true);
            this.swiperIns = null;
        }
    }

    setSwiperHeight() {
        const wrapper = this.carousel?.querySelector('.swiper-wrapper');
        if (wrapper) this.style.setProperty('--swiper-height', `${wrapper.offsetHeight}px`);
    }
}
if (!customElements.get('carousel-component')) {
    customElements.define('carousel-component', carouselComponent);
}
// collection content
class CollectionContent extends HTMLElement {
    constructor() {
        super();

        this.allContent = this.querySelector(`[aria-label="AllContent"]`);
        this.lessContent = this.querySelector(`[aria-label="LessContent"]`);

        if(!this.querySelector(`[aria-label="ShowMore"]`)) return;

        this.querySelector(`[aria-label="ShowMore"]`).addEventListener('click', () => this.showMore());
        this.querySelector(`[aria-label="ShowLess"]`).addEventListener('click', () => this.showLess());
    }

    showMore() {
        this.lessContent.classList.add('hidden');
        this.allContent.classList.remove('hidden');
    }

    showLess() {
        this.allContent.classList.add('hidden');
        this.lessContent.classList.remove('hidden');
    }
}
customElements.define('collection-content', CollectionContent);